import type { CashForecastWeek, CashPositionMonth } from '../api'
import { monthLabel } from '../format'
import { compactYen, plotValue } from './scale'

// Pure data helpers for the "Forecast vs actual" card. The with-pipeline
// switch is the component's state, so every helper takes it as an argument.
// Values are parsed for GEOMETRY and for the gap only (see scale.ts); every
// figure a person reads goes through compactYen.

export type LinePoint = { month: string; value: number }

/** Copies are newest first. Open on the one from four weeks back, else the oldest. */
export function defaultWeekIndex(weeks: CashForecastWeek[]): number | null {
  if (weeks.length === 0) return null
  return weeks.length > 4 ? 4 : weeks.length - 1
}

/** The saved forecast as a line, on the chosen column. */
export function snapshotLine(week: CashForecastWeek, showPipeline: boolean): LinePoint[] {
  return week.points.flatMap((p) => {
    const value = plotValue(showPipeline ? p.balance : p.balanceExPipeline)
    return value === null ? [] : [{ month: p.month, value }]
  })
}

const projectedValue = (m: CashPositionMonth, showPipeline: boolean): number | null =>
  plotValue(showPipeline ? m.projectedBalance : m.projectedBalanceExPipeline)

const actualValue = (m: CashPositionMonth): number | null =>
  m.pointKind === 'actual' ? plotValue(m.actualBalance) : null

/**
 * The balance as it stands now: real month-end balances, then the projection,
 * which starts from the actual it was projected from so the two joins up.
 */
export function nowLine(
  months: CashPositionMonth[],
  showPipeline: boolean,
): { actual: LinePoint[]; projected: LinePoint[] } {
  const actualPoints = months.flatMap((m) => {
    const value = actualValue(m)
    return value === null ? [] : [{ month: m.month, value }]
  })
  const projectedPoints = months.flatMap((m) => {
    if (m.pointKind !== 'projected') return []
    const value = projectedValue(m, showPipeline)
    return value === null ? [] : [{ month: m.month, value }]
  })
  if (projectedPoints.length === 0) return { actual: actualPoints, projected: [] }
  const anchorMonth = months.find((m) => m.pointKind === 'projected')?.projectionAnchorMonth
  const anchor = actualPoints.find((p) => p.month === anchorMonth)
  return { actual: actualPoints, projected: anchor ? [anchor, ...projectedPoints] : projectedPoints }
}

/** What the balance is for a month now: the actual, else the projection. */
function valueNow(month: string, months: CashPositionMonth[], showPipeline: boolean): number | null {
  const row = months.find((m) => m.month === month)
  if (row === undefined) return null
  return row.pointKind === 'projected' ? projectedValue(row, showPipeline) : actualValue(row)
}

/** Now minus what the copy said, for one month. Null when either side is missing. */
export function gapAt(
  month: string,
  week: CashForecastWeek,
  months: CashPositionMonth[],
  showPipeline: boolean,
): number | null {
  const was = snapshotLine(week, showPipeline).find((p) => p.month === month)
  const now = valueNow(month, months, showPipeline)
  return was === undefined || now === null ? null : now - was.value
}

// compactYen switches to "¥600k" under a million, which makes a headline read
// "¥28.4M ... +¥600k". The headline keeps millions down to ¥0.1M so every figure
// in the sentence is in the same unit; only a sliver under that falls to k.
function millionsYen(magnitude: number): string {
  return magnitude >= 100_000 && magnitude < 1_000_000_000
    ? `¥${(magnitude / 1_000_000).toFixed(1)}M`
    : compactYen(magnitude)
}
const yenText = (value: number): string => `${value < 0 ? '−' : ''}${millionsYen(Math.abs(value))}`
const signedYen = (value: number): string =>
  value === 0 ? yenText(0) : `${value < 0 ? '−' : '+'}${millionsYen(Math.abs(value))}`

/** 2026-10-12 → 12 Oct. */
const dayMonth = (date: string): string => `${Number(date.slice(8, 10))} ${monthLabel(date).split(' ')[0]}`

/** "Oct", or "Oct 2027" when it falls in a different year from the copy. */
function monthName(month: string, week: CashForecastWeek): string {
  const [name, year] = monthLabel(month).split(' ')
  return year === week.weekOf.slice(0, 4) ? `${name}` : `${name} ${year}`
}

/** The one-line takeaway above the chart. */
export function headline(week: CashForecastWeek, months: CashPositionMonth[], showPipeline: boolean): string {
  const suffix = week.origin === 'rebuilt' ? ' (rebuilt estimate)' : ''
  // Sits before the sentence's final full stop.
  const was = snapshotLine(week, showPipeline)
  const anchorMonths = new Set(week.points.filter((p) => p.isAnchor).map((p) => p.month))
  const ended = months
    .filter((m) => !anchorMonths.has(m.month) && actualValue(m) !== null && was.some((p) => p.month === m.month))
    .at(-1)
  if (ended !== undefined) {
    const expected = was.find((p) => p.month === ended.month)
    const had = actualValue(ended)
    if (expected !== undefined && had !== null) {
      return (
        `On ${dayMonth(week.weekOf)} we expected ${yenText(expected.value)} at end of ${monthName(ended.month, week)}. ` +
        `We had ${yenText(had)}: ${signedYen(had - expected.value)}${suffix}.`
      )
    }
  }
  const last = was.at(-1)
  if (last === undefined) return `The copy from ${dayMonth(week.weekOf)} has no forecast months${suffix}.`
  const gap = gapAt(last.month, week, months, showPipeline)
  const end = monthName(last.month, week)
  return gap === null
    ? `Since ${dayMonth(week.weekOf)}, there is no newer forecast for end of ${end}${suffix}.`
    : `Since ${dayMonth(week.weekOf)}, the forecast for end of ${end} has moved ${signedYen(gap)}${suffix}.`
}

/** Monday on or before a YYYY-MM-DD date. Date-only arithmetic, no time zones. */
function mondayOnOrBefore(date: string): string {
  const [y, m, d] = date.split('-').map(Number)
  const day = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1))
  day.setUTCDate(day.getUTCDate() - ((day.getUTCDay() + 6) % 7))
  return day.toISOString().slice(0, 10)
}

// This week's copy is taken by the nightly run, which saves a captured week on
// Mondays; give it until noon Monday (Tokyo) before calling it missing.
const MONDAY_GRACE_HOUR_JST = 12
const JST_OFFSET_MS = 9 * 60 * 60 * 1000

/** True when this week's Monday copy has not arrived (newest copy is older). */
export function copyIsMissing(weeks: CashForecastWeek[], now: Date): boolean {
  const newest = weeks[0]
  if (newest === undefined) return true
  // Tokyo has no daylight saving: a fixed +9h shift gives its wall clock.
  const jst = new Date(now.getTime() + JST_OFFSET_MS)
  const todayJst = jst.toISOString().slice(0, 10)
  const monday = mondayOnOrBefore(todayJst)
  if (newest.weekOf >= monday) return false
  return !(todayJst === monday && jst.getUTCHours() < MONDAY_GRACE_HOUR_JST)
}
